/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import { usePinnedBottomHeight } from '@hyphengroup/cms-components/hooks/usePinnedBottomHeight';
import classNames from 'classnames';
import { useState, useEffect, useRef } from 'react';
import { BrandId } from '../../utils/override';
import { pushDataLayer } from './cms/pushDataLayer';
import ChevronDown from './icons/ChevronDown';
import ChevronUp from './icons/ChevronUp';
import { PersonalLoanForm } from './PersonalLoanForm';
import { ComponentSearchStickyFooter } from '$/(cms)/_modules/cmsService/types';

interface SearchStickyFooterProps extends ComponentSearchStickyFooter {
  brand: BrandId;
}

export const SearchStickyFooter = ({
  brand,
  title,
  visible,
  content,
}: SearchStickyFooterProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosed, setIsClosed] = useState(true);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // What the bar covers while it is slid in, for the Sort & Filter button and
  // rail to stay clear of it.
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  usePinnedBottomHeight(bar, isVisible);

  const toggleSticky = () => {
    setIsClosed((prev) => !prev);
    pushDataLayer({
      event: 'form_interact',
      lead_form_name: 'sticky-policy-search-module',
      form_type: 'search form',
      action: !isClosed ? 'collapse' : 'expand',
      component_type: 'bottom-sticky',
      component_title: `${title} ${!isClosed ? 'Toggle_Collapse' : 'Toggle_Expand'}`,
    });
  };

  useEffect(() => {
    if (!visible) {
      setIsVisible(false);
      return;
    }

    const handleScroll = () => {
      if (isFooterVisible) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsVisible(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFooterVisible, visible]);

  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          setIsVisible(false);
        }
      },
      { threshold: 0.1 },
    );

    observerRef.current.observe(footer);

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <div
      ref={setBar}
      className={classNames(
        'fixed bottom-0 left-0 z-50 w-full shadow transition-transform duration-300 ease-out',
        {
          'translate-y-0': isVisible,
          'translate-y-full': !isVisible,
        },
      )}
    >
      <div className="w-full overflow-hidden p-0">
        <div className="h-full flex-col rounded-none bg-fill/card sm:flex-row">
          <div
            role="button"
            tabIndex={0}
            className="relative w-full cursor-pointer border-b border-borders/default text-left"
            onClick={toggleSticky}
          >
            <div className="mx-auto flex w-full flex-col justify-center gap-spacing/gap/y-xs px-spacing/gap/x py-spacing/margin/y bg-transparent md:max-w-[990px]">
              <h3 className="mhg-alpha-typography-title-1 flex text-fill/primary-dark">
                {title}
                <div
                  role="button"
                  tabIndex={0}
                  className="ml-auto md:hidden"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSticky();
                  }}
                >
                  {isClosed ? <ChevronUp /> : <ChevronDown />}
                </div>
              </h3>
              <RichText className="mhg-alpha-typography-body-2 text-text/secondary">
                {content}
              </RichText>
            </div>
            <div
              role="button"
              tabIndex={0}
              className="absolute right-8 top-1/2 hidden -translate-y-1/2 items-center justify-center md:flex"
              onClick={(e) => {
                e.stopPropagation();
                toggleSticky();
              }}
            >
              {isClosed ? <ChevronUp /> : <ChevronDown />}
            </div>
          </div>

          {!isClosed && (
            <div className="mx-auto flex w-full flex-col justify-center px-spacing/gap/x py-spacing/gap/y bg-transparent md:max-w-[990px]">
              <div className="flex flex-col justify-between md:flex-row md:items-center">
                <PersonalLoanForm title="title" brand={brand} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
